import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../config';

const BottomNavBar = ({ currentPath }) => {
  const navigate = useNavigate();
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackCategory, setFeedbackCategory] = useState('Suggestion');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  
  const navItems = [
    { path: '/dashboard', label: 'Home', icon: '🏠' },
    { path: '/learn', label: 'Learn', icon: '📚' },
    { path: '/settings', label: 'Settings', icon: '⚙️' },
  ];

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    if (!feedbackMessage.trim()) return;
    
    setSubmitting(true);
    setError('');
    
    try {
      await axios.post(`${API}/feedback`, {
        category: feedbackCategory,
        message: feedbackMessage
      });
      setSubmitted(true);
      setTimeout(() => {
        setShowFeedbackModal(false);
        setSubmitted(false);
        setFeedbackMessage('');
        setFeedbackCategory('Suggestion');
      }, 2000);
    } catch (err) {
      setError('Failed to send feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openFeedbackModal = () => {
    setShowFeedbackModal(true);
    setSubmitted(false);
    setError('');
  };

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-2 py-2 z-40">
        <div className="max-w-lg mx-auto flex justify-around items-center">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center py-2 px-4 rounded-xl transition ${
                currentPath === item.path
                  ? 'text-emerald-600 bg-emerald-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="text-xl mb-1">{item.icon}</span>
              <span className="text-xs font-medium">{item.label}</span>
            </button>
          ))}
          
          {/* Feedback Button */}
          <button
            onClick={openFeedbackModal}
            className="flex flex-col items-center py-2 px-4 rounded-xl transition text-gray-500 hover:text-gray-700 hover:bg-gray-50"
          >
            <span className="text-xl mb-1">💬</span>
            <span className="text-xs font-medium">Feedback</span>
          </button>
        </div>
      </nav>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowFeedbackModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">✅</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h3>
                <p className="text-gray-600">Your feedback has been sent.</p>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl">💬</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">Share Your Feedback</h2>
                  <p className="text-gray-600 text-sm">We&apos;d love to hear from you!</p>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmitFeedback} className="space-y-4">
                  {/* Category Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      value={feedbackCategory}
                      onChange={(e) => setFeedbackCategory(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
                    >
                      <option value="Bug">🐛 Bug Report</option>
                      <option value="Suggestion">💡 Suggestion</option>
                      <option value="Question">❓ Question</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Your Message
                    </label>
                    <textarea
                      value={feedbackMessage}
                      onChange={(e) => setFeedbackMessage(e.target.value)}
                      placeholder="Tell us what's on your mind..."
                      rows={4}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting || !feedbackMessage.trim()}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-lg transition disabled:opacity-50"
                  >
                    {submitting ? 'Sending...' : 'Send Feedback'}
                  </button>
                </form>

                <button
                  onClick={() => setShowFeedbackModal(false)}
                  className="w-full mt-3 text-gray-600 hover:text-gray-900 text-sm"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BottomNavBar;
